import { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { setClientCurrentClickedItem, setClientVariantType } from "../../store/slices/pdpSlice";
import { requestToFetchBlockedDatesAction } from "../../store/slices/common/shippingDateAndSlotSelection";
import { CAKE_TYPE } from "../../constants/pageConstants/pdpConstants";
import { COUNTRIES } from "../../constants/common/common";
import { cleverTapCommonEvents } from "../../analytics/clevertapConstants";
import { debounce } from "../../utils/common";
import useAnalytics from "../../analytics/useAnalytics";
import useProductAnalyticsPayload from "./useProductAnalyticsPayload";

/**
 * Resolves the product's egg/eggless variant option (if it has one) and a handler to toggle it.
 *
 * @returns {{ show: boolean, label?: string, isEggless?: boolean, onToggle?: Function }}
 */
const useEggVariantSwitch = () => {
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();
  const { commonCleverTapData, eventDataForCdp, catalogId, pincode, currentSelectedProductId } =
    useProductAnalyticsPayload();

  const { clientVariants, clientVariantLabel, clientVariantType, flavorList, clientVariantFlavour } = useSelector(
    (state) => ({
      clientVariants: state.pdp.clientVariants,
      clientVariantLabel: state.pdp.clientVariantLabel,
      clientVariantType: state.pdp.clientVariantType,
      flavorList: state.pdp.flavorList,
      clientVariantFlavour: state.pdp.clientVariantFlavour,
    }),
    shallowEqual,
  );

  const parentItem = clientVariantLabel
    ? clientVariants?.find((item) => item[clientVariantLabel]?.[clientVariantType])
    : undefined;

  const onSelectVariant = useMemo(() => {
    return debounce((item) => {
      dispatch(setClientVariantType(item));

      const productIds = flavorList?.length
        ? parentItem[clientVariantLabel][item]?.[clientVariantFlavour]?.productId
        : parentItem[clientVariantLabel][item]?.productId;

      if (flavorList?.length) {
        dispatch(setClientCurrentClickedItem(parentItem[clientVariantLabel][item]?.[clientVariantFlavour]));
      } else {
        dispatch(setClientCurrentClickedItem(parentItem[clientVariantLabel][item]));
      }

      if (pincode && catalogId === COUNTRIES.INDIA) {
        dispatch(requestToFetchBlockedDatesAction({ productId: productIds, pinCode: pincode }));
      }

      trackEvent(
        cleverTapCommonEvents.variantSelected,
        { ...commonCleverTapData, is_egg: item?.toUpperCase() === CAKE_TYPE.WITH_EGG ?? false },
        ["clevertap"],
      );

      if (currentSelectedProductId) {
        trackEvent(
          cleverTapCommonEvents?.variantSelected,
          {
            ...eventDataForCdp,
            products: [{ ...eventDataForCdp.products[0], productId: parentItem[clientVariantLabel][item]?.productId }],
          },
          ["CDP"],
        );
      }
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    parentItem,
    clientVariantLabel,
    flavorList,
    clientVariantFlavour,
    commonCleverTapData,
    eventDataForCdp,
    currentSelectedProductId,
    pincode,
    catalogId,
  ]);

  if (!parentItem) {
    return { show: false };
  }

  const categoryValue = Object.keys(parentItem[clientVariantLabel]);
  const alternateVariant = categoryValue?.find((item) => item !== clientVariantType);
  const isEggless = clientVariantType?.toUpperCase() === CAKE_TYPE.EGG_LESS;

  return {
    show: true,
    label: clientVariantType,
    isEggless,
    onToggle: () => alternateVariant && onSelectVariant(alternateVariant),
  };
};

export default useEggVariantSwitch;
